import type { CartItemInterface, OptionDataInterface } from '~~/server/types/CartInterface';
import { defineAsyncComponent, ref } from 'vue'
import { useDebounceFn } from '@vueuse/core'
import { useMessages } from '~/composables/useMessages'
import { useGlobalDialog } from '@/composables/useGlobalDialog'

const AddToCartDialogComponent = defineAsyncComponent(
    () => import('~/components/dialogs/AddToCartDialogComponent.vue')
)
const AgeConfirmationDialogComponent = defineAsyncComponent(
    () => import('~/components/dialogs/AgeConfirmationDialogComponent.vue')
)

const AGE18_CONFIRMATION_REQUIRED = 'age18_confirmation_required'

type CartRequestError = {
    response?: {
        status?: number
        _data?: {
            code?: string
            data?: { code?: string; url?: string }
            url?: string
        }
    }
}

export function useCart() {
    const cartStore = useCartStore()
    const adding = ref<Record<string, boolean>>({})
    const { show_error, show_success } = useMessages()
    const localePath = useLocalePath()
    const { t } = useI18n()
    const { openDialog } = useGlobalDialog()
    const { decision: age18Decision, confirm: confirmAge18 } = useAgeConfirmation()

    const addToCart = async (
        id: number,
        code: string,
        conversion: number,
        count: number,
        options: Record<number, number | number[] | string> = {},
        options_data: OptionDataInterface = {},
        redirect_to = true,
        convertToOrder: boolean = false,
        reservation: { starts_at: string } | null = null,
        subscription_months: 1 | 3 | 6 | 12 | null = null,
    ) => {
        if (!code) {
            throw new Error('Invalid product')
        }
        try {
            adding.value[id] = true
            const item = {
                product_id: id,
                code,
                conversion,
                options,
                options_data,
                ...(subscription_months ? { subscription_months } : {}),
                ...(reservation ? { reservation } : {})
            }
            const cartItem = {
                finger_print: getFingerPrint(item as CartItemInterface),
                count,
                ...item,
            } as CartItemInterface

            const saveCartItem = async (canRequestAgeConfirmation = true): Promise<CartItemInterface | null> => {
                try {
                    return await cartStore.addToCart(
                        cartItem,
                        convertToOrder,
                        age18Decision.value ?? null,
                    )
                } catch (error: unknown) {
                    const e = error as CartRequestError
                    const errorData = e.response?._data
                    const errorCode = errorData?.code ?? errorData?.data?.code

                    if (canRequestAgeConfirmation && errorCode === AGE18_CONFIRMATION_REQUIRED) {
                        const confirmed = await openDialog<boolean>(AgeConfirmationDialogComponent, {
                            title: t('age18_confirmation_title'),
                            width: '540px',
                        })

                        if (!confirmed) {
                            return null
                        }

                        confirmAge18()
                        return await saveCartItem(false)
                    }

                    if (errorCode === 'booking_unavailable') {
                        await show_error(error)
                        throw error
                    }
                    if (errorCode === 'booking_required') {
                        const productUrl = errorData?.data?.url ?? errorData?.url
                        if (productUrl) await navigateTo(localePath(productUrl.startsWith('/') ? productUrl : '/' + productUrl))
                        else await show_error(error)
                        return null
                    }
                    if (e.response?.status === 409) {
                        if (redirect_to) {
                            await navigateTo(localePath('/' + e.response._data?.url))
                        } else {
                            await show_error(error)
                        }
                    } else {
                        await show_error(error)
                    }

                    throw error
                }
            }

            const response = await saveCartItem()

            if (!response) {
                return false
            }

            if (response?.status === 'not_changed') {
                await show_error(response)
                return false
            }

            const responseDesign = response?.design ?? 'notification'

            if (responseDesign === 'silent') return true

           else if (responseDesign === 'notification') {
                await show_success(response.message!)
            } else {
                void openDialog(AddToCartDialogComponent, {
                    title: t('product_added_successfully'),
                    width: '600px',
                    props: {
                        product: id,
                    },
                })
            }

            return true
        } finally {
            adding.value[id] = false
        }
    }

    const decrement = async (item: CartItemInterface) => {
        if (item.count <= 1) return
        item.count--
        await changeCount(item)
    }

    const increment = async (item: CartItemInterface) => {
        item.count++
        await changeCount(item)
    }

    const changeCount = useDebounceFn(async (item: CartItemInterface) => {
        if (!item.count || item.count < 1) {
            item.count = 1
        }

        try {
            adding.value[item.cart_item_id] = true
            const res = await cartStore.changeCount(item)

            if (res?.status === 'updated') await show_success(res.message!)
            if (res?.status === 'changed') await show_error(res)
        } catch (e: any) {
            await show_error(e)
        } finally {
            adding.value[item.cart_item_id] = false
        }
    }, 300)

    const removeItem = async (item: CartItemInterface) => {
        try {
            adding.value[item.cart_item_id] = true
            await cartStore.removeItem(item)
        } catch (e: any) {
            await show_error(e.message || e.statusText || 'Something went wrong')
        } finally {
            adding.value[item.cart_item_id] = false
        }
    }

    const normalizeOptions = (options: Record<number, number | number[]>): string =>
        Object.keys(options)
            .filter((key: any) => (Array.isArray(options[key]) && options[key].length) || typeof options[key] === 'number')
            .map(Number)
            .sort((a, b) => a - b)
            .map((key) => {
                const value = options[key]
                if (Array.isArray(value)) {
                    const sorted = [...value].sort((a, b) => a - b)
                    return `${key}=[${sorted.join(',')}]`
                }
                return `${key}=${value}`
            })
            .join('|')

    const getFingerPrint = (item: CartItemInterface): string => {
        const optionsStr = normalizeOptions(item.options)
        const dataStr = Object.entries(item.options_data)
            .map(([key, data]) => `${key}=${data.type}:${data.value}`)
            .join('|')
        return `${item.product_id}::${item.code}::${optionsStr}::${dataStr}${item.reservation ? "::" + item.reservation.starts_at : ""}${item.subscription_months ? "::subscription:" + item.subscription_months : ""}`
    }

    return { addToCart, decrement, increment, changeCount, removeItem, adding }
}
